import {useState} from 'react'
import {useDialogContext, useDialogStore} from '@ariakit/react'
import dayjs from 'dayjs'
import {
    Dialog,
    DialogBody,
    DialogDismiss,
    DialogFooter,
    DialogHeader,
    DialogHeading,
    DialogProvider
} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Button} from '@/components/ui/button/Button'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {InputDate} from '@/components/commons/input-date/InputDate'
import {Text} from '@/components/ui/text/Text'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Trans, useTranslation} from '@/translations/i18n'
import {toast} from '@/utilities/toast/toast'
import {useConfirmSiarExportTransmission} from '@/features/projects/siar-bulk-export/queries/useConfirmSiarExportTransmission'
import {SiarExport} from '@/features/projects/siar-bulk-export/types'

export const ConfirmTransmissionDialog = ({
    siarExport,
    isOpen,
    onClose
}: {
    siarExport: SiarExport
    isOpen: boolean
    onClose: () => void
}) => {
    const dialogStore = useDialogStore({
        open: isOpen,
        setOpen: nextOpen => {
            if (!nextOpen) onClose()
        }
    })

    return (
        <DialogProvider store={dialogStore}>
            <Dialog width="400px" overflowY="unset" onClick={e => e.stopPropagation()}>
                {isOpen && <ConfirmTransmissionDialogContent siarExport={siarExport} onClose={onClose} />}
            </Dialog>
        </DialogProvider>
    )
}

const ConfirmTransmissionDialogContent = ({siarExport, onClose}: {siarExport: SiarExport; onClose: () => void}) => {
    const {t} = useTranslation()
    const dialog = useDialogContext()
    const minDate = dayjs(siarExport.dateFrom).startOf('day')
    const maxDate = dayjs().endOf('day')
    const [transmissionDate, setTransmissionDate] = useState<Date | null>(() => {
        const today = dayjs()
        return (today.isBefore(minDate) ? minDate : today).toDate()
    })

    const isDateOutOfRange = !!(
        transmissionDate &&
        (dayjs(transmissionDate).isBefore(minDate) || dayjs(transmissionDate).isAfter(maxDate))
    )

    const mutation = useConfirmSiarExportTransmission({
        onSuccess: () => {
            toast.success(t('projects:bulk_siar_export_transmission_success'))
            dialog?.setOpen(false)
            onClose()
        },
        onError: () => {
            toast.error(t('errors:default'))
        }
    })

    const handleConfirm = () => {
        if (!transmissionDate || isDateOutOfRange) return
        mutation.mutate({
            id: siarExport.id,
            selectedProjectIds: [],
            exceptedProjectIds: [],
            transmissionDate: dayjs(transmissionDate).format('YYYY-MM-DD')
        })
    }

    return (
        <>
            <DialogHeader>
                <DialogHeading>{t('projects:bulk_siar_export_confirm_transmission_title')}</DialogHeading>
                <DialogDismiss />
            </DialogHeader>
            <DialogBody>
                <Flexbox direction="column" gap={4}>
                    <Text size="textSm" palette="neutral" shade="700">
                        <Trans
                            i18nKey="projects:bulk_siar_export_confirm_transmission_description"
                            values={{name: siarExport.fileName}}
                            components={{b: <b />}}
                        />
                    </Text>
                    <InputDate
                        label={t('projects:bulk_siar_export_transmission_date_label')}
                        value={transmissionDate}
                        onChange={date => setTransmissionDate(date)}
                        placeholder="gg/mm/aaaa"
                        fullWidth
                        portal
                        disableBeforeDate={minDate.toDate()}
                        disableAfterDate={maxDate.toDate()}
                        errorMessage={
                            isDateOutOfRange ? t('projects:bulk_siar_export_transmission_date_out_of_range') : undefined
                        }
                    />
                    <Text size="textXs" palette="neutral" shade="600">
                        {t('projects:bulk_siar_export_action_irreversible')}
                    </Text>
                </Flexbox>
            </DialogBody>
            <DialogFooter>
                <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
                    {t('commons:cancel')}
                </Button>
                <Button
                    variant="primaryNeutral"
                    onClick={handleConfirm}
                    disabled={!transmissionDate || isDateOutOfRange || mutation.isPending}
                >
                    {t('projects:bulk_siar_export_confirm_transmission')}
                    {mutation.isPending && <Spinner />}
                </Button>
            </DialogFooter>
        </>
    )
}
